// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// updated  : 2026-06-19
// protected: NO
// =============================================================================
// Stock Opname: mulai (auto-isi stok sistem) -> hitung fisik -> POST (selisih = ADJUSTMENT).
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/v2/components/statusMap";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ClipboardCheck, Play, Save, CheckCircle2, FolderOpen, Info } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { WAREHOUSES, STORES, isOwner, allowedLocations } from "@/v2/components/helpers";
import { ListPager, loadStoredPageSize } from "@/v2/components/ListPager";

const keyOf = (l) => `${l.product_id}|${l.size}`;

export default function OpnamePage() {
  const { api, me } = useV2Auth();
  const owner = isOwner(me?.role);
  const ALL_LOC = allowedLocations(me);   // lokasi sesuai scope (bukan semua region)
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => loadStoredPageSize("opname"));
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const listTopRef = useRef(null);
  const [products, setProducts] = useState({});
  const [loc, setLoc] = useState(ALL_LOC[0] || WAREHOUSES[0]);
  const [note, setNote] = useState("");
  const [detail, setDetail] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadList = useCallback(async (p = 1, size = pageSize) => {
    setListLoading(true);
    try {
      const r = await api(`/api/v2/opname?page=${p}&page_size=${size}`);
      const data = await r.json().catch(() => ({}));
      if (r.ok) { setList(data.items || []); setMeta({ total: data.total || 0, pages: data.pages || 1 }); setPage(data.page || p); }
      else setList([]);
    } catch (_) { setList([]); } finally { setListLoading(false); }
  }, [api, pageSize]);
  useEffect(() => { loadList(1); }, [loadList]);
  const goPage = (p) => { loadList(p); if (listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const changePageSize = (s) => { setPageSize(s); loadList(1, s); };

  useEffect(() => {
    api("/api/v2/products").then(async (r) => {
      if (r.ok) { const d = await r.json(); const m = {}; d.forEach((p) => { m[p.id] = p; }); setProducts(m); }
    });
  }, [api]);

  const applyDetail = (d) => {
    setDetail(d);
    const e = {};
    (d.lines || []).forEach((l) => { e[keyOf(l)] = l.counted_qty; });
    setEdits(e);
  };

  const openDetail = async (id) => {
    setLoadingDetail(true);
    try { const r = await api(`/api/v2/opname/${id}`); if (r.ok) applyDetail(await r.json()); }
    finally { setLoadingDetail(false); }
  };

  const start = async () => {
    setBusy(true);
    try {
      const r = await api("/api/v2/opname", { method: "POST", body: JSON.stringify({ location: loc, note }) });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal memulai opname"); return; }
      toast.success(`Opname ${d.opname_no} dimulai (${d.lines?.length || 0} baris)`);
      setNote(""); applyDetail(d); loadList();
    } finally { setBusy(false); }
  };

  const saveCount = async () => {
    if (!detail?.lines?.length) return;
    const counts = detail.lines.map((l) => ({
      product_id: l.product_id, size: l.size,
      counted_qty: Number(edits[keyOf(l)] ?? l.counted_qty),
    }));
    setBusy(true);
    try {
      const r = await api(`/api/v2/opname/${detail.id}/count`, { method: "PUT", body: JSON.stringify({ counts }) });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal menyimpan hitungan"); return; }
      toast.success("Hitungan tersimpan"); applyDetail(d); loadList();
    } finally { setBusy(false); }
  };

  const post = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const r = await api(`/api/v2/opname/${detail.id}/post`, { method: "POST", body: JSON.stringify({}) });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal posting opname"); return; }
      toast.success(`Opname diposting · masuk ${d.adjustments?.in || 0}, keluar ${d.adjustments?.out || 0}`);
      applyDetail(d); loadList();
    } finally { setBusy(false); }
  };

  const editable = !owner && detail && (detail.status === "OPEN" || detail.status === "COUNTING");
  const pname = (id) => products[id]?.name || (id ? `${id.slice(0, 8)}…` : "-");

  return (
    <div className="space-y-5" data-testid="v2-opname-page">
      <h2 className="text-2xl font-semibold flex items-center gap-2"><ClipboardCheck className="w-6 h-6" />Stock Opname</h2>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Mulai opname */}
        {owner ? (
          <Card className="p-5 rounded-2xl grid place-items-center text-center text-sm text-muted-foreground min-h-[160px]" data-testid="v2-opname-readonly">
            Mode Owner: tampilan read-only (audit/monitoring). Mulai/hitung/posting opname dinonaktifkan.
          </Card>
        ) : (
        <Card className="p-5 rounded-2xl space-y-3">
          <h3 className="font-semibold flex items-center gap-2"><Play className="w-4 h-4 text-primary" />Mulai Opname Baru</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Lokasi</Label>
              <Select value={loc} onValueChange={setLoc}>
                <SelectTrigger className="mt-1" data-testid="v2-opname-loc"><SelectValue /></SelectTrigger>
                <SelectContent>{ALL_LOC.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Catatan (opsional)</Label>
              <Input className="mt-1" value={note} onChange={(e) => setNote(e.target.value)} data-testid="v2-opname-note" />
            </div>
          </div>
          <Button onClick={start} disabled={busy} data-testid="v2-opname-start">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}Mulai
          </Button>
          <p className="text-xs text-muted-foreground">Sistem otomatis mengisi stok terkini item <span className="font-medium">JUAL</span> per produk/size sebagai dasar hitung (item MoU dikecualikan).</p>
        </Card>
        )}

        {/* Daftar opname */}
        <Card className="p-5 rounded-2xl" ref={listTopRef}>
          <h3 className="font-semibold mb-3">Riwayat Opname ({meta.total})</h3>
          <div className="max-h-72 overflow-y-auto">
            <Table>
              <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Lokasi</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {list.map((o) => (
                  <TableRow key={o.id} data-testid="v2-opname-row">
                    <TableCell className="font-mono text-xs">{o.opname_no}</TableCell>
                    <TableCell className="text-sm">{o.location}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openDetail(o.id)} data-testid="v2-opname-open"><FolderOpen className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ListPager
            page={page} pageSize={pageSize} total={meta.total} pages={meta.pages} loading={listLoading}
            onPageChange={goPage} onPageSizeChange={changePageSize}
            storageKey="opname" testid="opname-pager"
          />
        </Card>
      </div>

      {/* Detail / penghitungan */}
      {loadingDetail && <Card className="p-6 rounded-2xl"><Loader2 className="w-5 h-5 animate-spin" /></Card>}
      {detail && !loadingDetail && (
        <Card className="p-5 rounded-2xl space-y-4" data-testid="v2-opname-detail">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold font-mono">{detail.opname_no}</h3>
              <Badge variant="outline">{detail.location}</Badge>
              <StatusBadge status={detail.status} />
            </div>
            {editable && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={saveCount} disabled={busy} data-testid="v2-opname-save"><Save className="w-4 h-4 mr-1" />Simpan Hitungan</Button>
                <Button size="sm" onClick={post} disabled={busy} data-testid="v2-opname-post"><CheckCircle2 className="w-4 h-4 mr-1" />Posting</Button>
              </div>
            )}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Merchandize</TableHead><TableHead>Size</TableHead>
                <TableHead className="text-right">Sistem</TableHead>
                <TableHead className="text-right">Fisik</TableHead>
                <TableHead className="text-right">Selisih</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(detail.lines || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4 text-sm">Tidak ada stok di lokasi ini.</TableCell></TableRow>}
                {(detail.lines || []).map((l) => {
                  const counted = Number(edits[keyOf(l)] ?? l.counted_qty);
                  const diff = counted - l.system_qty;
                  return (
                    <TableRow key={keyOf(l)} data-testid="v2-opname-line">
                      <TableCell className="text-sm">{pname(l.product_id)}</TableCell>
                      <TableCell>{l.size}</TableCell>
                      <TableCell className="text-right tabular-nums">{l.system_qty}</TableCell>
                      <TableCell className="text-right">
                        {editable ? (
                          <Input type="number" min={0} value={edits[keyOf(l)] ?? l.counted_qty}
                            onChange={(e) => setEdits({ ...edits, [keyOf(l)]: e.target.value })}
                            className="h-8 w-24 ml-auto text-right" data-testid="v2-opname-count-input" />
                        ) : <span className="tabular-nums">{l.counted_qty}</span>}
                      </TableCell>
                      <TableCell className={`text-right tabular-nums font-medium ${diff === 0 ? "text-muted-foreground" : diff > 0 ? "text-[hsl(var(--badge-success))]" : "text-destructive"}`}>
                        {diff > 0 ? `+${diff}` : diff}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobile: card list penghitungan */}
          <div className="md:hidden space-y-3" data-testid="v2-opname-cards">
            {(detail.lines || []).length === 0 && (
              <div className="text-center text-muted-foreground py-6 text-sm rounded-xl border border-dashed">Tidak ada stok di lokasi ini.</div>
            )}
            {(detail.lines || []).map((l) => {
              const counted = Number(edits[keyOf(l)] ?? l.counted_qty);
              const diff = counted - l.system_qty;
              return (
                <div key={keyOf(l)} className="rounded-xl border p-3 space-y-2" data-testid="v2-opname-line-m">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium min-w-0 truncate">{pname(l.product_id)}</span>
                    <Badge variant="outline" className="shrink-0">{l.size}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 items-end">
                    <div>
                      <div className="text-[11px] text-muted-foreground">Sistem</div>
                      <div className="tabular-nums text-sm">{l.system_qty}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-muted-foreground">Fisik</div>
                      {editable ? (
                        <Input type="number" min={0} value={edits[keyOf(l)] ?? l.counted_qty}
                          onChange={(e) => setEdits({ ...edits, [keyOf(l)]: e.target.value })}
                          className="h-10 w-full text-right mt-0.5" data-testid="v2-opname-count-input-m" />
                      ) : <div className="tabular-nums text-sm">{l.counted_qty}</div>}
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] text-muted-foreground">Selisih</div>
                      <div className={`tabular-nums text-sm font-semibold ${diff === 0 ? "text-muted-foreground" : diff > 0 ? "text-[hsl(var(--badge-success))]" : "text-destructive"}`}>
                        {diff > 0 ? `+${diff}` : diff}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
